import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Divider,
  CircularProgress,
  InputAdornment,
  IconButton,
  Switch,
  FormControlLabel,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import LogoSettings from '../components/LogoSettings';
import PatientSharingSettings from '../components/PatientSharingSettings';
import LetterheadSettings from '../components/LetterheadSettings';
import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage as policyErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function apiErrorMessage(err) {
  return policyErrorMessage(err, err?.message || 'Request failed');
}

export default function Settings() {
  const { applySession, completePasswordChange, user } = useAuth();
  const isAdmin = user?.role === 'clinic_admin';
  // Clinical profile: specialty for providers & staff; education for providers.
  const showSpeciality = ['provider', 'staff', 'clinic_admin'].includes(user?.role);
  const showEducation = user?.role === 'provider';
  const [loading, setLoading] = useState(true);
  const [providersCanAddPatients, setProvidersCanAddPatients] = useState(false);
  const [savingClinic, setSavingClinic] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [information, setInformation] = useState('');
  const [speciality, setSpeciality] = useState([]);
  const [education, setEducation] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/auth/me`)
      .then(({ data }) => {
        if (!cancelled) {
          setFullName(data.full_name || '');
          setEmail(data.email || '');
          setInformation(data.information || '');
          setSpeciality(Array.isArray(data.speciality) ? data.speciality : []);
          setEducation(Array.isArray(data.education) ? data.education : []);
        }
      })
      .catch(() => toast.error('Could not load profile'))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/admin/clinic-settings`)
      .then(({ data }) => { if (!cancelled) setProvidersCanAddPatients(!!data?.providers_can_add_patients); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin]);

  const handleToggleProvidersCanAddPatients = async (e) => {
    const next = e.target.checked;
    setProvidersCanAddPatients(next);
    setSavingClinic(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/v1/admin/clinic-settings`, {
        providers_can_add_patients: next,
      });
      setProvidersCanAddPatients(!!data?.providers_can_add_patients);
      toast.success('Clinic settings updated');
    } catch (err) {
      setProvidersCanAddPatients(!next); // revert on failure
      toast.error(apiErrorMessage(err));
    } finally {
      setSavingClinic(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = { full_name: fullName, information };
      if (showSpeciality) payload.speciality = speciality;
      if (showEducation) payload.education = education;
      const { data } = await axios.patch(`${API_URL}/api/v1/auth/me`, payload);
      // The reply always carries the updated profile. A rename also re-issues the
      // session, and the CSRF token that comes with it is picked up by ../session.js.
      if (data.user) {
        applySession(data.user);
      }
      toast.success('Profile updated');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const problem = passwordProblem(newPassword);
    if (problem) {
      toast.error(problem);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setSavingPw(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/me/password`, {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      // The change revokes every earlier token, this one included — adopt the new one.
      completePasswordChange(res.data);
      toast.success('Password updated. A confirmation email has been sent to your address.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSavingPw(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h4" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 700, mb: 0.5 }}>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your profile and manage your password.
      </Typography>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(4, 47, 46, 0.12)', mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Profile
        </Typography>
        <Box component="form" onSubmit={handleSaveProfile}>
          <Stack spacing={2}>
            <TextField
              label="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
              autoComplete="name"
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              fullWidth
              autoComplete="email"
              InputProps={{ readOnly: true }}
              helperText="Email cannot be changed here. Contact your clinic administrator if you need a different address."
            />
            {showSpeciality && (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={speciality}
                onChange={(e, v) => setSpeciality(v)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Specialty"
                    placeholder="Type a specialty and press Enter"
                    helperText="Add one or more. Press Enter after each."
                  />
                )}
              />
            )}
            {showEducation && (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={education}
                onChange={(e, v) => setEducation(v)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Education & training"
                    placeholder="e.g. MD, Stanford — press Enter"
                    helperText="Degrees, residencies, certifications. Press Enter after each."
                  />
                )}
              />
            )}
            <TextField
              label="About you"
              value={information}
              onChange={(e) => setInformation(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder="Background, interests, languages spoken, or other information you want on file."
              helperText="Optional. Visible to your organization where profile details are shown."
            />
            <Button type="submit" variant="contained" disabled={savingProfile} sx={{ alignSelf: 'flex-start' }}>
              {savingProfile ? 'Saving…' : 'Save profile'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <LogoSettings />

      <PatientSharingSettings />

      <LetterheadSettings />

      {isAdmin && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(4, 47, 46, 0.12)', mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Clinic settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Settings that apply to everyone in your clinic.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={providersCanAddPatients}
                onChange={handleToggleProvidersCanAddPatients}
                disabled={savingClinic}
              />
            }
            label="Allow providers to add patients"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            When on, providers get an “Add Patient” option and can register patients (assigned to themselves).
            When off, only clinic admins and staff can register patients.
          </Typography>
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(4, 47, 46, 0.12)' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Change password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your current password, then your new password twice. We will email you when your password changes.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box component="form" onSubmit={handleChangePassword}>
          <Stack spacing={2}>
            <TextField
              label="Current password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              autoComplete="current-password"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
              required
              helperText={PASSWORD_RULES_TEXT}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowNewPassword((v) => !v)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm new password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="outlined" disabled={savingPw} sx={{ alignSelf: 'flex-start' }}>
              {savingPw ? 'Updating…' : 'Update password'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
